/**
 * Skill Library Service — wraps /api/admin/skills/
 * Phase 4.7 — WQ-164
 */

export interface Skill {
  id: string;
  name: string;
  slug: string;
  version: string;
  description: string | null;
  install_path: string;
  module_count: number;
  module_inventory: Record<string, unknown> | null;
  is_active: boolean;
  registered_by: string | null;
  created_at: string;
  updated_at: string;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

export async function listSkills(
  authFetch: FetchFn,
  activeOnly: boolean = true
): Promise<Skill[]> {
  const res = await authFetch(`/api/admin/skills/?active_only=${activeOnly}`);
  if (!res.ok) throw new Error(`Failed to list skills: ${res.status}`);
  return res.json();
}

export async function getSkill(authFetch: FetchFn, id: string): Promise<Skill> {
  const res = await authFetch(`/api/admin/skills/${id}`);
  if (!res.ok) throw new Error(`Failed to get skill: ${res.status}`);
  return res.json();
}

export async function registerSkill(
  authFetch: FetchFn,
  data: Omit<Skill, 'id' | 'is_active' | 'registered_by' | 'created_at' | 'updated_at'>
): Promise<Skill> {
  const res = await authFetch('/api/admin/skills/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to register skill: ${res.status}`);
  return res.json();
}

export async function updateSkill(
  authFetch: FetchFn,
  id: string,
  data: Partial<Skill>
): Promise<Skill> {
  const res = await authFetch(`/api/admin/skills/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update skill: ${res.status}`);
  return res.json();
}

export async function syncSkills(
  authFetch: FetchFn,
  skills: Array<{ name: string; slug: string; version: string; description?: string; install_path: string; module_count: number }>
): Promise<{ inserted: number; updated: number; unchanged: number; total: number }> {
  const res = await authFetch('/api/admin/skills/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skills }),
  });
  if (!res.ok) throw new Error(`Failed to sync skills: ${res.status}`);
  return res.json();
}
